import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { 
  Briefcase, 
  Building, 
  MapPin, 
  Clock, 
  DollarSign, 
  Calendar,
  Eye,
  Trash2,
  Filter,
  Search,
  CheckCircle,
  XCircle,
  Clock as ClockIcon,
  AlertCircle
} from 'lucide-react';
import { Link } from 'react-router-dom';

const Applications = () => {
  const { user } = useAuth();
  const [applications, setApplications] = useState([]);
  const [filteredApplications, setFilteredApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('all');

  // Dummy data for demonstration
  useEffect(() => {
    const dummyApplications = [
      {
        id: 1,
        jobId: 1,
        jobTitle: 'Frontend Developer',
        company: 'TechCorp Solutions',
        location: 'Mumbai, Maharashtra',
        salary: '₹6-8 LPA',
        appliedDate: '2024-01-15',
        status: 'applied',
        statusText: 'Application Submitted',
        lastUpdated: '2024-01-15',
        description: 'We are looking for a skilled Frontend Developer to join our team...',
        logo: 'https://via.placeholder.com/50'
      },
      {
        id: 2,
        jobId: 2,
        jobTitle: 'Backend Developer',
        company: 'DataFlow Systems',
        location: 'Bangalore, Karnataka',
        salary: '₹8-12 LPA',
        appliedDate: '2024-01-10',
        status: 'shortlisted',
        statusText: 'Shortlisted for Interview',
        lastUpdated: '2024-01-12',
        description: 'Join our backend team to build scalable server-side applications...',
        logo: 'https://via.placeholder.com/50'
      },
      {
        id: 3,
        jobId: 3,
        jobTitle: 'UI/UX Designer',
        company: 'Creative Studios',
        location: 'Delhi, NCR',
        salary: '₹5-7 LPA',
        appliedDate: '2024-01-08',
        status: 'rejected',
        statusText: 'Application Rejected',
        lastUpdated: '2024-01-11',
        description: 'Create beautiful and intuitive user interfaces for our products...',
        logo: 'https://via.placeholder.com/50'
      },
      {
        id: 4,
        jobId: 4,
        jobTitle: 'DevOps Engineer',
        company: 'CloudTech Solutions',
        location: 'Pune, Maharashtra',
        salary: '₹10-15 LPA',
        appliedDate: '2024-01-05',
        status: 'interview',
        statusText: 'Interview Scheduled',
        lastUpdated: '2024-01-14',
        description: 'Manage and optimize our cloud infrastructure and deployment pipelines...',
        logo: 'https://via.placeholder.com/50'
      },
      {
        id: 5,
        jobId: 5,
        jobTitle: 'Data Scientist',
        company: 'Analytics Pro',
        location: 'Hyderabad, Telangana',
        salary: '₹12-18 LPA',
        appliedDate: '2024-01-03',
        status: 'offered',
        statusText: 'Offer Received',
        lastUpdated: '2024-01-16',
        description: 'Build machine learning models and analyze complex datasets...',
        logo: 'https://via.placeholder.com/50'
      }
    ];

    setApplications(dummyApplications);
    setFilteredApplications(dummyApplications);
    setLoading(false);
  }, []);

  const statusOptions = [
    { value: 'all', label: 'All Applications' },
    { value: 'applied', label: 'Applied' },
    { value: 'shortlisted', label: 'Shortlisted' },
    { value: 'interview', label: 'Interview' },
    { value: 'offered', label: 'Offered' },
    { value: 'rejected', label: 'Rejected' }
  ];

  const getStatusIcon = (status) => {
    switch (status) {
      case 'applied':
        return <ClockIcon className="h-5 w-5 text-blue-500" />;
      case 'shortlisted':
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case 'interview':
        return <AlertCircle className="h-5 w-5 text-yellow-500" />;
      case 'offered':
        return <CheckCircle className="h-5 w-5 text-green-600" />;
      case 'rejected':
        return <XCircle className="h-5 w-5 text-red-500" />;
      default:
        return <ClockIcon className="h-5 w-5 text-gray-500" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'applied':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
      case 'shortlisted':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'interview':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      case 'offered':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'rejected':
        return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200';
    }
  };

  const handleSearch = () => {
    let filtered = applications.filter(app => {
      const matchesSearch = app.jobTitle.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           app.company.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           app.description.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesStatus = selectedStatus === 'all' || app.status === selectedStatus;

      return matchesSearch && matchesStatus;
    });

    setFilteredApplications(filtered);
  };

  useEffect(() => {
    handleSearch();
  }, [searchTerm, selectedStatus, applications]);

  const handleWithdrawApplication = (applicationId) => {
    // TODO: Implement withdraw application functionality
    console.log('Withdrawing application:', applicationId);
  };

  const getStatusCount = (status) => {
    return applications.filter(app => status === 'all' ? true : app.status === status).length;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
            My Applications
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            Track the status of your job applications
          </p>
        </motion.div>

        {/* Status Overview */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-8"
        >
          {statusOptions.map((option) => (
            <div
              key={option.value}
              className="bg-white dark:bg-gray-800 rounded-lg p-4 text-center cursor-pointer hover:shadow-md transition-shadow"
              onClick={() => setSelectedStatus(option.value)}
            >
              <div className="text-2xl font-bold text-gray-900 dark:text-white mb-1">
                {getStatusCount(option.value)}
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400">
                {option.label}
              </div>
            </div>
          ))}
        </motion.div>

        {/* Search and Filters */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 mb-8"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
              <input
                type="text"
                placeholder="Search applications..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>

            {/* Status Filter */}
            <select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            >
              {statusOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </motion.div>

        {/* Results Count */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="mb-6"
        >
          <p className="text-gray-600 dark:text-gray-400">
            Showing {filteredApplications.length} of {applications.length} applications
          </p>
        </motion.div>

        {/* Applications List */}
        <div className="space-y-6">
          {filteredApplications.map((application, index) => (
            <motion.div
              key={application.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 hover:shadow-md transition-shadow"
            >
              <div className="flex items-start justify-between">
                <div className="flex items-start space-x-4">
                  <img
                    src={application.logo}
                    alt={application.company}
                    className="w-12 h-12 rounded-lg object-cover"
                  />
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-2">
                      <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                        {application.jobTitle}
                      </h3>
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(application.status)}`}>
                        {application.statusText}
                      </span>
                    </div>
                    
                    <div className="flex items-center space-x-4 text-sm text-gray-600 dark:text-gray-400 mb-3">
                      <div className="flex items-center space-x-1">
                        <Building className="h-4 w-4" />
                        <span>{application.company}</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <MapPin className="h-4 w-4" />
                        <span>{application.location}</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <DollarSign className="h-4 w-4" />
                        <span>{application.salary}</span>
                      </div>
                    </div>

                    <p className="text-gray-600 dark:text-gray-400 mb-4 line-clamp-2">
                      {application.description}
                    </p>

                    <div className="flex items-center space-x-4 text-sm text-gray-500 dark:text-gray-400">
                      <div className="flex items-center space-x-1">
                        <Calendar className="h-4 w-4" />
                        <span>Applied: {new Date(application.appliedDate).toLocaleDateString()}</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <Clock className="h-4 w-4" />
                        <span>Updated: {new Date(application.lastUpdated).toLocaleDateString()}</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex flex-col space-y-2">
                  {getStatusIcon(application.status)}
                  <div className="flex space-x-2">
                    <Link
                      to={`/jobs/${application.jobId}`}
                      className="p-2 text-gray-400 hover:text-blue-500 transition-colors"
                      title="View Job Details"
                    >
                      <Eye className="h-4 w-4" />
                    </Link>
                    {application.status === 'applied' && (
                      <button
                        onClick={() => handleWithdrawApplication(application.id)}
                        className="p-2 text-gray-400 hover:text-red-500 transition-colors"
                        title="Withdraw Application"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {/* Status Timeline */}
              <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
                <div className="flex items-center space-x-4">
                  <div className="flex items-center space-x-2">
                    <CheckCircle className="h-4 w-4 text-green-500" />
                    <span className="text-sm text-gray-600 dark:text-gray-400">Application Submitted</span>
                  </div>
                  {application.status !== 'applied' && (
                    <>
                      <div className="flex-1 h-px bg-gray-300 dark:bg-gray-600"></div>
                      <div className="flex items-center space-x-2">
                        {getStatusIcon(application.status)}
                        <span className="text-sm text-gray-600 dark:text-gray-400">{application.statusText}</span>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* No Results */}
        {filteredApplications.length === 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-12"
          >
            <div className="text-gray-400 dark:text-gray-500 mb-4">
              <Briefcase className="h-16 w-16 mx-auto" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
              No applications found
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              {applications.length === 0 
                ? "You haven't applied to any jobs yet. Start your job search today!"
                : "Try adjusting your search criteria or filters"
              }
            </p>
            {applications.length === 0 && (
              <Link
                to="/jobs"
                className="btn-primary"
              >
                Browse Jobs
              </Link>
            )}
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default Applications;
